// Shared by usePdfPages and renderers (GPC, TXN) to keep page assignments identical.
// Budget kept below the parity point where width-scale stops binding (~1507px) so
// font sizes stay consistent across all description pages.

// Defaults match current PDF export styles; pass layout overrides when font sizes
// or margins change to avoid silent layout regressions.
export const DESCRIPTION_PAGE_BUDGET_PX = 1450;
export const DESCRIPTION_BLOCK_OVERHEAD_PX = 80;
export const DESCRIPTION_LINE_HEIGHT_PX = 38;
export const DESCRIPTION_CHARS_PER_LINE = 230;

export interface DescriptionEstimateLayout {
  blockOverheadPx?: number;
  lineHeightPx?: number;
  charsPerLine?: number;
}

export const estimateDescriptionHeightPx = (
  text: string | null | undefined,
  layout: DescriptionEstimateLayout = {}
): number => {
  const blockOverheadPx = layout.blockOverheadPx ?? DESCRIPTION_BLOCK_OVERHEAD_PX;
  const lineHeightPx = layout.lineHeightPx ?? DESCRIPTION_LINE_HEIGHT_PX;
  const charsPerLine = layout.charsPerLine ?? DESCRIPTION_CHARS_PER_LINE;
  const length = text?.length ?? 0;
  const lineCount = Math.max(1, Math.ceil(length / charsPerLine));
  return blockOverheadPx + lineCount * lineHeightPx;
};

export const packDescriptionsForPdf = <T>(
  items: readonly T[],
  getDescription: (item: T) => string | null | undefined,
  budgetPx: number = DESCRIPTION_PAGE_BUDGET_PX,
  layout: DescriptionEstimateLayout = {}
): T[][] => {
  const chunks: T[][] = [];
  let current: T[] = [];
  let currentHeight = 0;

  items.forEach(item => {
    const itemHeight = estimateDescriptionHeightPx(getDescription(item), layout);
    if (current.length > 0 && currentHeight + itemHeight > budgetPx) {
      chunks.push(current);
      current = [];
      currentHeight = 0;
    }
    current.push(item);
    currentHeight += itemHeight;
  });

  if (current.length > 0) {
    chunks.push(current);
  }

  return chunks;
};
